import axios from "axios";
import * as Constants from "../../data/StringConstants";
import { baseURL, orgRootURL } from "../../utils/urlHelpers";

let _internalAxios = axios.create({ baseURL: baseURL(), headers: {'pragma' : 'no-cache', 'cache-control' : 'no-cache'}});

/**
 * @summary - When we are in certain contexts outside of the react project (i.e. legacy editor)
 * the baseURL configured is not usable for interacting with out controllers. For certain
 * components that are being rendered across the entire application that need to make
 * requests to the API, this is the axios object to use.
*/
 let _externalContextAxios = axios.create({ baseURL: orgRootURL(), headers: {'pragma' : 'no-cache', 'cache-control' : 'no-cache'}});

/**
 * @public
 * @description - "Folder" related, codified, explicit endpoint
 * contract implementation
 * @version 2.0.0
 * @see LibraryAware.Web::Scripts::React::src::api::LibraryAwareHttpClient - parent
 * details
 */
export const FoldersApi = {
	/**
	 * @private
	 * @deprecated - DO NOT USE IN APPCODE
	 * @description - replaces the _internalAxios client with a new version. This is a concession
	 * we have to make in order offer the required testing flexibility required by use of `act` as
	 * well as `shallow||mount(<component />, { FolderApi }) injection
	 */
	_rebuildInternalClient: newClient => (_internalAxios = newClient),
	/**
	 * @public
	 * @description - Retrieves all Folders, for an Organization, deemed "Active"
	 * @param {object} params - required arguments
	 * @returns {Promise}
	 */
	fetchAllActiveFoldersForAnOrg(params) {
		return _internalAxios.get(
			Constants.URI_RESOURCE.getActivePromotionsForOrg,
			{
				params: params
			}
		);
	},
	/**
	 * @public
	 * @description - Retrieves all Folders, for an Organization, deemed "Archived"
	 * @returns {Promise}
	 */
	fetchAllArchivedFoldersForAnOrg() {
		return _internalAxios.get(
			Constants.URI_RESOURCE.getArchivedPromotionsForOrg
		);
	},

	/**
	 * @public
	 * @description - Retrieves all Folders, for a User, which are deemed "Active"
	 * @param {object} params - required arguments
	 * @returns {Promise}
	 */
	fetchAllActiveFoldersForAUser(params) {
		return _internalAxios.get(
			Constants.URI_RESOURCE.getActivePromotionsForAUser,
			{
				params: params
			}
		);
	},

	/**
	 * @public
	 * @description - Retrieves all Folders, for a User, which are deemed "Starred"
	 * @param {object} params - required arguments
	 * @returns {Promise}
	 */
	fetchAllActiveStarredFoldersForAUser(params) {
		return _internalAxios.get(
			Constants.URI_RESOURCE.getActiveStarredPromotionsForAUser,
			{
				params: params
			}
		);
	},

	/**
	 * @public
	 * @description - Retrieves items, matching the param filter(s)"
	 * @param {object} params - required "filter" arguments
	 * @returns {Promise}
	 */
	fetchItems(params) {
		const completeParams = {
			page: 1,
			size: 50,
			starFilterType: 0,
			...params
		};

		return _internalAxios.get(Constants.URI_RESOURCE.getDocuments, {
			params: completeParams
		});
	},

	/**
	 * @public
	 * @description - Copy an existing item
	 * @param {object} params - payload necessary to perform document copy
	 * @param {number} params.id - ID of document to copy
	 * @param {string} params.name - New name to give copy
	 * @param {string[guid]} params.newContextID - The new Promotion/Folder context to assign the facimile to
	 */
	copyItemToFolder(params) {
		return _internalAxios.post(Constants.URI_RESOURCE.copyItem, params)
	},

	/**
	 * @public
	 * @description - Retrieves the "folder context"
	 * @returns {Promise}
	 */
	getCreateFolderContext() {
		return _internalAxios.get(Constants.URI_RESOURCE.createFolder);
	},

	/**
	 * @public
	 * @description - Requests that a folder be created, per parameter specification
	 * @param {{name: string, description: string, defaultBrandingSetId: int}} params
	 * @returns {Promise}
	 */
	createFolder(params) {
		return _internalAxios.post(Constants.URI_RESOURCE.createFolder, params);
	},

	/**
	 * @public
	 * @description - Retrieves specified folder's details
	 * @param {{id: number}} params - Id of the folder to retrieve details for
	 * @returns {Promise}
	 */
	getFolderDetails(folderId) {
		return _internalAxios.get(Constants.URI_RESOURCE.getFolder, {
			params: { id: folderId }
		});
	},

	/**
	 * @param {{folderId: number}} params
	 */
	archiveFolder(folderId) {
		return _internalAxios.post(Constants.URI_RESOURCE.archiveFolder, {
			id: folderId
		});
	},

	/**
	 * @param {{folderIds: [number]}} params
	 */
	bulkUnarchive(folderIds) {
		return _internalAxios.post(Constants.URI_RESOURCE.bulkUnarchive, {
			ids: folderIds
		});
	},

	/**
	 * @param {{folderId: number}} params
	 */
	deleteFolder(folderId) {
		return _internalAxios.post(Constants.URI_RESOURCE.deleteFolder, {
			id: folderId
		});
	},

	updateFolder(payload) {
		return _internalAxios.put(Constants.URI_RESOURCE.updateFolder, payload);
	},

	/**
	 * @param {{itemId: number}} params
	 */
	deleteItem(itemId) {
		return _internalAxios.post(Constants.URI_RESOURCE.deleteItem, {
			id: itemId
		});
	},

	/**
	 * @public
	 * @description - Retrieves items, matching the param filter(s)"
	 * @param {object} params - required "filter" arguments
	 * @returns {Promise}
	 */
	fetchActiveItemsForOrg(params) {
		const completeParams = {
			page: 1,
			size: 50,
			...params
		};

		return _internalAxios.get(Constants.URI_RESOURCE.getActiveItemsForOrg, {
			params: completeParams
		});
	},

	/**
	 * @param {{folderId: number}} folderId
	 */
	fetchAvailableFiltersForFolderItems(folderId) {
		return _internalAxios.get(
			Constants.URI_RESOURCE.getAvailableFiltersForFolderItems,
			{
				params: { id: folderId }
			}
		);
	},
	/*
	 * @public
	 * @description - Toggle the star state of an item i.e. could be a document or a folder
	 * @param {string} correlationId Id of folder OR item/document, whichever is being starred/unstarred
	 * @param {bool} isStarred Current state of the folder/item.
	 */
	toggleItemStar(correlationId, isStarred, starContext, subContext="") {
		const params = {
			ItemId: correlationId,
			SubContext: subContext,
			StarContext: starContext,
			IsUserStarred: isStarred
		};

		return _internalAxios.post(
			Constants.URI_RESOURCE.toggleItemStar,
			params
		);
	},

	/*
	 * @public
	 * @description - Get the available org tree in json format to populate the org picker component
	 */
	getOrganizationTree() {
		return _externalContextAxios.get(Constants.URI_RESOURCE.getOrgTree);
	},

	/*
	 * @public
	 * @description - Get the name of an org by its id
	 */
	getOrganizationName() {
		return _externalContextAxios.get(Constants.URI_RESOURCE.getOrgName);
	},
};
